import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ChartBarIcon,
  CogIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { GiGoldBar } from "react-icons/gi";
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Customers', href: '/customers', icon: UsersIcon },
  { name: 'Loans', href: '/loans', icon: DocumentTextIcon },
  { name: 'Payments', href: '/payments', icon: CreditCardIcon },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

const adminNavigation = [
  { name: 'Users', href: '/users', icon: UserGroupIcon },
];

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
      <div
        className="flex min-h-0 flex-1 flex-col shadow-lg relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(252,211,77,0.95) 0%, rgba(245,158,11,0.95) 45%, rgba(217,119,6,0.95) 100%)`,
        }}
      >
        {/* Subtle jewelry sketch pattern using inline SVG */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cg fill='none' stroke='%2392400e' stroke-opacity='0.35' stroke-width='1.2'%3E%3Cpath d='M80 10l20 30-20 30-20-30z'/%3E%3Ccircle cx='120' cy='120' r='16'/%3E%3Cpath d='M20 120c10-8 22-8 32 0 10 8 22 8 32 0'/%3E%3Cpath d='M100 20c8 6 12 14 12 24'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '200px 200px',
          }}
        />
        {/* Logo */}
        <div 
          className="flex h-16 flex-shrink-0 items-center px-4 border-b relative overflow-hidden"
          style={{
            borderColor: 'rgba(217, 119, 6, 0.35)',
            backgroundImage: 'linear-gradient(180deg, rgba(252,211,77,0.95) 0%, rgba(245,158,11,0.95) 45%, rgba(217,119,6,0.95) 100%)'
          }}
        >
          {/* Same jewelry pattern as header */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cg fill='none' stroke='%2392400e' stroke-opacity='0.35' stroke-width='1.2'%3E%3Cpath d='M80 10l20 30-20 30-20-30z'/%3E%3Ccircle cx='120' cy='120' r='16'/%3E%3Cpath d='M20 120c10-8 22-8 32 0 10 8 22 8 32 0'/%3E%3Cpath d='M100 20c8 6 12 14 12 24'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px',
            }}
          />
          <GiGoldBar  className="h-8 w-8 text-primary-700 relative z-10" />
          <div className="ml-3 relative z-10">
            <h1 className="text-xl font-bold text-primary-900">Gold Loan</h1>
            <p className="text-xs text-primary-800">Management System</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-1 flex-col overflow-y-auto">
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive: active }) =>
                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      active || isActive(item.href)
                        ? 'bg-primary-900/20 text-primary-900 shadow'
                        : 'text-gray-700 hover:bg-primary-900/10 hover:text-primary-900'
                    }`
                  }
                >
                  <Icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive(item.href)
                        ? 'text-primary-700'
                        : 'text-primary-700/80 group-hover:text-primary-700'
                    }`}
                  />
                  {item.name}
                </NavLink>
              );
            })}
            
            {/* Admin navigation */}
            {user?.role === 'admin' && (
              <div className="pt-4 border-t border-yellow-200/70">
                <div className="px-2 mb-2">
                  <h3 className="text-xs font-semibold text-primary-700 uppercase tracking-wider">
                    Administration
                  </h3>
                </div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive: active }) =>
                        `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                          active || isActive(item.href)
                            ? 'bg-primary-900/20 text-primary-900 shadow'
                            : 'text-gray-700 hover:bg-primary-900/10 hover:text-primary-900'
                        }`
                      }
                    >
                      <Icon
                        className={`mr-3 h-5 w-5 flex-shrink-0 ${
                          isActive(item.href)
                            ? 'text-primary-700'
                            : 'text-primary-700/80 group-hover:text-primary-700'
                        }`}
                      />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </nav>
        </div>

        {/* User info */}
        <div className="flex-shrink-0 border-t border-yellow-200/70 p-4 bg-white/80 backdrop-blur">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center shadow">
                <span className="text-sm font-medium text-primary-700">
                  {user?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-primary-700 truncate">
                {user?.full_name || 'User'}
              </p>
              <p className="text-xs text-primary-600 truncate capitalize">
                {user?.role || 'Staff'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
